/**
 * studentActivityAdapter — l'activité que l'élève exécute, telle que l'API la sert → ce que la page
 * d'activité montre (en-tête, énoncé, sans support), la correction et l'enchaînement d'après.
 *
 * Portage littéral de l'ancien écran :
 *  - le titre et la ligne de la matière viennent d'`OneStepActivityHeader.js` (le titre du support à
 *    défaut celui de l'activité ; le chapitre à défaut la matière) ;
 *  - l'énoncé est celui de la fiche (`toUIActivityStatement`, la même lecture que le parent :
 *    `MaterialDisplayModal`) ;
 *  - « Ajouter un énoncé » est le lien de `TimerActivityStep.js` (« Ajouter » + `undefinedMaterialSlug`,
 *    recopié de `student/activity/todo/ActivityPicker.js` : `student/**` est interdit aux ports) ;
 *  - la copie corrigée est celle de `CorrectionSequence` (les pages rendues, l'image de correction en
 *    première, les points).
 */
import { type UIActivityStatement, type UIActivityWork, type UISubject } from "../../../ui/types"
import {
    toUIActivityStatement,
    toUICorrectionPoints,
    type ApiCorrectionPoint,
} from "../../parent/Activities/activityDetailsAdapter"
import { toUISubject, type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"

/** Formes lâches (`ports/CLAUDE.md` § 5). */
export type ApiRunActivity = {
    id?: number | null
    title?: string | null
    slug?: string | null
    status?: string | null
    duration?: number | null
    estimated_duration?: number | null
    school_subject_id?: number | null
    activity_template_id?: number | null
    activityTemplateId?: number | null
    finished_at?: string | null
    isDeleted?: boolean
    material?: {
        id?: number | null
        title?: string | null
        text?: string | null
        url?: string | null
        attachments?: (string | { uri?: string })[] | null
        media_type?: string | null
        school_subject_id?: number | null
        figure_description?: string | null
        figure_svg?: string | null
    } | null
    student_chapter?: { chapter?: { name?: string | null } | null } | null
    activity_logs?: { id?: number | null; action?: string | null; work_attachment?: string | null }[] | null
    prerequisite_quiz?: unknown
} & Record<string, unknown>

export type ApiActivityTemplate = {
    id?: number | null
    steps?: { type?: string | null; description_init?: string | null }[] | null
}

/** Les étapes du modèle de l'activité (`findItemInListWithId(activity_template_id, activity_templates)`). */
export function activityTemplateSteps(
    activity: ApiRunActivity | null | undefined,
    templates: ApiActivityTemplate[] | null | undefined
): ApiActivityTemplate["steps"] {
    const id = activity?.activity_template_id ?? activity?.activityTemplateId
    if (id == null) return null
    return (templates ?? []).find((template) => template?.id === id)?.steps ?? null
}

export type StudentActivityHeading = { title: string; subject: UISubject; chapterName?: string }

/** L'en-tête : le titre, la matière, le chapitre (`OneStepActivityHeader`). */
export function toStudentActivityHeading(
    activity: ApiRunActivity | null | undefined,
    subjects: ApiSchoolSubject[] | null | undefined
): StudentActivityHeading {
    const subjectId = activity?.school_subject_id ?? activity?.material?.school_subject_id
    const subject = (subjects ?? []).find((candidate) => candidate?.id === subjectId) ?? null
    const chapterName = activity?.student_chapter?.chapter?.name ?? undefined
    return {
        title: activity?.material?.title ?? activity?.title ?? "Activité",
        subject: toUISubject(subject),
        chapterName: chapterName != null && chapterName !== "" ? chapterName : undefined,
    }
}

/**
 * L'énoncé, et la figure servie en SVG (`figure_svg`) que l'ancien `MaterialContentDisplay` dessinait
 * sous le texte (`MaterialSvgDisplay`, seulement quand le support a son id).
 */
export function toStudentActivityStatement(activity: ApiRunActivity | null | undefined): UIActivityStatement {
    const material = activity?.material ?? null
    const statement = toUIActivityStatement(material)
    const svg = material?.id != null ? material?.figure_svg : null
    return svg != null && svg !== "" ? { ...statement, figureSvg: svg } : statement
}

const MATERIAL_SLUGS: Record<string, string> = {
    video_course: "vidéo",
    lesson_card: "cours",
    flash_revision: "cours",
    go_exo: "énoncé",
    go_homework_oral: "énoncé",
}
const MATERIAL_ARTICLES: Record<string, string> = {
    video_course: "une ",
    lesson_card: "un ",
    flash_revision: "un ",
    go_exo: "un ",
    go_homework_oral: "un ",
}

/** « Ajouter un énoncé », « Ajouter une vidéo » : le libellé de `TimerActivityStep`, sans support. */
export function addMaterialLabel(slug: string | null | undefined): string {
    const noun = slug != null ? MATERIAL_SLUGS[slug] : undefined
    return `Ajouter ${noun != null ? `${MATERIAL_ARTICLES[slug as string]}${noun}` : "un support"}`
}

/** La copie corrigée : l'image de correction d'abord, puis les pages ; les points ancrés sur la copie. */
export function toCorrectedWork(correction: {
    image: string | null
    points: unknown[] | null
    workImages: { uri: string }[] | null
}): UIActivityWork {
    const pages = (correction.workImages ?? []).map((image) => image.uri)
    const points =
        correction.image != null ? toUICorrectionPoints(correction.points as ApiCorrectionPoint[], pages.length) : []
    const anchored = points.length > 0 && pages.length > 0 && points.every((point) => point.anchor != null)
    return {
        images: correction.image != null ? [correction.image, ...pages] : pages,
        hasCorrection: correction.image != null,
        correctionPoints: points.length > 0 ? points : undefined,
        copyPages: anchored ? pages : undefined,
    }
}

/** La suite d'une activité rendue : les activités à faire du moment, sans celle qui vient de l'être. */
export function remainingTodo(todo: ApiRunActivity[] | null | undefined, finishedId: number | null): ApiRunActivity[] {
    return (todo ?? []).filter(
        (activity) => activity?.id !== finishedId && !activity?.isDeleted && !activity?.finished_at
    )
}
